import React, { startTransition } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/firebaseAuth';
import appLogo from '../../media/logo/logo_variant6.png';
import { FacebookIcon, TelegramIcon, WhatsappIcon } from 'react-share';

import { isRenter } from '../../types/userTypes';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const handleLinkClick = (path: string) => {
    startTransition(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      // Place any asynchronous or suspending logic here, if necessary.
      // For example, if it triggers navigation or state updates.
      navigate(path); // Assuming you have a `navigate` function
    });
  };

  return (
    <footer className="bg-gray-800 text-white mt-12">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
        <div className="pb-8 mb-8 border-b border-gray-700">
          <Link
            to="/"
            className="flex items-center no-underline cursor-pointer"
            onClick={() => handleLinkClick('/')}
          >
            <img src={appLogo} alt="RentSmart.ie Logo" className="h-20 w-20 text-blue-500" />
            <span className="ml-3 text-2xl font-bold text-white">RentSmart.ie</span>
          </Link>
          <p className="mt-2 text-sm text-gray-300">
            Find your perfect home with ease. RentSmart.ie connects you with the best rental
            properties across Ireland.
          </p>
        </div>
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="grid grid-cols-2 gap-8 xl:col-span-2">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  For Renters
                </h3>
                <ul className="mt-4 space-y-4">
                  {isRenter(userProfile) && (
                    <li>
                      <Link
                        to="/tenant-toolkit"
                        className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                        onClick={() => handleLinkClick('/tenant-toolkit')}
                      >
                        Tenant Toolkit
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link
                      to="/prices/discover"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/prices/discover')}
                    >
                      Discover Price Trends
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/browse"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/browse')}
                    >
                      Browse Listings
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/discover-areas"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/discover-areas')}
                    >
                      Discover Exciting Areas
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/things-to-do"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/things-to-do')}
                    >
                      Things to Do in Ireland
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/property-tools/property-alerts"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/property-tools/property-alerts')}
                    >
                      Get property alerts to your inbox
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/property-tools"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/property-tools')}
                    >
                      Property Tools
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/bookmarks"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/bookmarks')}
                    >
                      Saved Listings
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/renter-guide"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/renter-guide')}
                    >
                      Renter's Guide
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/blog/irish-tenant-rights-2026"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/blog/irish-tenant-rights-2026')}
                    >
                      Irish Tenant Rights 2026
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/report-listing"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/report-listing')}
                    >
                      Report a listing
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  For Property Owners
                </h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link
                      to="/home-valuation"
                      className="text-base text-gray-300 hover:text-white no-underline"
                      onClick={() => handleLinkClick('/home-valuation')}
                    >
                      Home Valuation Tool
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/self-build"
                      className="text-base text-gray-300 hover:text-white no-underline"
                      onClick={() => handleLinkClick('/self-build')}
                    >
                      Self-Build Cost 2026
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/new-vs-old"
                      className="text-base text-gray-300 hover:text-white no-underline"
                      onClick={() => handleLinkClick('/new-vs-old')}
                    >
                      New Build vs Old Build
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/seller-checklist"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/seller-checklist')}
                    >
                      Seller Checklist
                    </Link>
                  </li>
                  {currentUser && (
                    <>
                      <li>
                        <Link
                          to="/add-listing"
                          className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                          onClick={() => handleLinkClick('/add-listing')}
                        >
                          List a Property
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/landlord-dashboard"
                          className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                          onClick={() => handleLinkClick('/landlord-dashboard')}
                        >
                          Listing Dashboard
                        </Link>
                      </li>
                    </>
                  )}
                  <li>
                    <Link
                      to="/landlord-toolkit"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/landlord-toolkit')}
                    >
                      Landlord Toolkit
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/property-tools"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/property-tools')}
                    >
                      Property Tools
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/landlord-guide"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/landlord-guide')}
                    >
                      Landlord's Guide
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/blog/irish-landlord-rules-2026"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/blog/irish-landlord-rules-2026')}
                    >
                      Irish Landlord Rules 2026
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/landlord-resources/rental-agreement-templates"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() =>
                        handleLinkClick('/landlord-resources/rental-agreement-templates')
                      }
                    >
                      Rental Resources
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  Company
                </h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link
                      to="/about-us"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/about-us')}
                    >
                      About Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/contact')}
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/safety"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/safety')}
                    >
                      Safety
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/help-center"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/help-center')}
                    >
                      Help center
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/blog"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/blog')}
                    >
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/faqs"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/faqs')}
                    >
                      FAQs
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  Resources
                </h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link
                      to="/schools-around"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/schools-around')}
                    >
                      Schools Around Neighborhoods
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/housing-reports"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/housing-reports')}
                    >
                      Housing Reports
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/city-comparisons"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/city-comparisons')}
                    >
                      Irish City Comparisons
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/rights"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/rights')}
                    >
                      Can I Be Evicted? 2026 Rules
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/new-vs-old"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/new-vs-old')}
                    >
                      New Build vs Old Build Matrix
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/compare/renting-in-dublin-vs-commuter-towns"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() =>
                        handleLinkClick('/compare/renting-in-dublin-vs-commuter-towns')
                      }
                    >
                      Dublin vs Commuter Trade-Offs
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/cost-of-living"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/cost-of-living')}
                    >
                      Cost of Living Ireland
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/salary"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/salary')}
                    >
                      Is My Salary Good in Ireland?
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/salary-after-tax"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/salary-after-tax')}
                    >
                      Salary After Tax Ireland
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/affordability"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/affordability')}
                    >
                      Rent Affordability by County
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/tools/rent-increase-notice-generator"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/tools/rent-increase-notice-generator')}
                    >
                      RPZ Notice Generator
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/landlord-insights"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/landlord-insights')}
                    >
                      Landlord & Agent Reports
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/explore-categories"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/explore-categories')}
                    >
                      Explore Categories
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/terms-of-service"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/terms-of-service')}
                    >
                      Terms of Service
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy-policy"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/privacy-policy')}
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/community"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/community')}
                    >
                      Join Our Community
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/community-guidelines"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/community-guidelines')}
                    >
                      Community Guidelines
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/cookie-policy"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/cookie-policy')}
                    >
                      Cookie Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/accessibility-statement"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/accessibility-statement')}
                    >
                      Accessibility Statement
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/digital-services-act"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/digital-services-act')}
                    >
                      Digital Services Act
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/data-requests"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/data-requests')}
                    >
                      Data requests
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/cookie-preferences"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/cookie-preferences')}
                    >
                      Manage Preferences
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/newsletter-preferences"
                      className="text-base text-gray-300 hover:text-white no-underline cursor-pointer"
                      onClick={() => handleLinkClick('/newsletter-preferences')}
                    >
                      Newsletter Preferences
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 border-t border-gray-700 pt-8 md:flex md:items-center md:justify-between">
          {/* Social Icons */}
          <div className="flex space-x-6 md:order-2">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/groups/1295007891494794"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300"
            >
              <span className="sr-only">Facebook</span>
              <FacebookIcon size={32} round className="h-8 w-8 text-blue-600" />
            </a>

            {/* Telegram */}
            <a
              href="https://t.me/rentsmart_ie"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300"
            >
              <span className="sr-only">Telegram</span>
              <TelegramIcon size={32} round className="h-8 w-8 text-blue-500" />
            </a>

            {/* WhatsApp */}
            <a
              href="https://chat.whatsapp.com/JQXsWjrrybP8covAaVggnD"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300"
            >
              <span className="sr-only">WhatsApp</span>
              <WhatsappIcon size={32} round className="h-8 w-8 text-green-500" />
            </a>
          </div>

          {/* Footer Text */}
          <div className="mt-8 md:mt-0 md:order-1">
            <p className="text-base text-gray-400">
              &copy; {new Date().getFullYear()} RentSmart.ie. All rights reserved.
            </p>
            <p className="mt-2 text-sm text-gray-300">Built with love in Ireland 🇮🇪❤️</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
